import { useRef, useState } from 'react';
import { rovingTabIndex, useRovingRadioGroup } from '../components/rovingFocus';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  CheckIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { SAVED_FILTER_EXPR_MAX, SAVED_FILTER_NAME_MAX, type SavedFilterIcon } from '@xphoto/shared';
import { useI18n } from '../i18n';
import {
  SAVED_FILTER_ICON_LABEL_KEY,
  SAVED_FILTER_ICON_ORDER,
  SavedFilterGlyph,
} from './saved-filter-icons';

/**
 * Saisie NOM + ICÔNE d'un filtre enregistré (création ou « Enregistrer sous… » de l'éditeur).
 * Vraie boîte de dialogue calquée sur les autres modales (focus initial, `Échap` ferme,
 * clic sur le fond ferme ; le caller l'enveloppe d'un `<Portal>`). Le nom est requis (1..80) ;
 * l'icône est choisie dans l'allow-list partagée (`savedFilterIconSchema`) ou « aucune ».
 * L'icône est signalée par une COCHE + `aria-checked` (jamais la couleur seule). L'expression
 * enregistrée est passée par le caller (affichée en aperçu tronqué, non éditable ici).
 */

interface SaveFilterDialogProps {
  /** Expression (texte canonique) qui sera enregistrée — affichée en aperçu (lecture seule). */
  expr: string;
  initialName?: string;
  initialIcon?: SavedFilterIcon | null;
  busy: boolean;
  error?: string | undefined;
  onSave: (name: string, icon: SavedFilterIcon | null) => void;
  onClose: () => void;
}

export function SaveFilterDialog({
  expr,
  initialName = '',
  initialIcon = null,
  busy,
  error,
  onSave,
  onClose,
}: SaveFilterDialogProps): React.JSX.Element {
  const { t } = useI18n();
  const [name, setName] = useState(initialName);
  const [icon, setIcon] = useState<SavedFilterIcon | null>(initialIcon);
  // Choix local d'icône (enregistré au submit) : la sélection SUIT le focus.
  const onIconKeyDown = useRovingRadioGroup('auto');
  const nameRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y({ dialogRef, initialFocusRef: nameRef, onEscape: !busy ? onClose : undefined });

  const trimmed = name.trim();
  // Rien à enregistrer si aucune expression n'est active (ni `expr` avancé, ni axes simples).
  const emptyExpr = expr.trim() === '';
  const canSave =
    trimmed.length > 0 && trimmed.length <= SAVED_FILTER_NAME_MAX && !emptyExpr && !busy;
  // Message affiché : la règle « rien à enregistrer » prime sur l'erreur d'API éventuelle.
  const shownError = emptyExpr ? t('savedFilters.errorEmptyExpr') : error;

  const submit = (): void => {
    if (!canSave) return;
    onSave(trimmed, icon);
  };

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('savedFilters.saveTitle')}
        className="flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="text-lg font-semibold text-fg">{t('savedFilters.saveTitle')}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            title={t('savedFilters.cancel')}
            aria-label={t('savedFilters.cancel')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col gap-4 overflow-y-auto p-4 text-sm text-fg">
          <div className="flex flex-col gap-1">
            <label htmlFor="save-filter-name" className="font-semibold text-fg">
              {t('savedFilters.nameLabel')}
            </label>
            <input
              id="save-filter-name"
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={SAVED_FILTER_NAME_MAX}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder={t('savedFilters.namePlaceholder')}
              className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            />
          </div>

          {/* Aperçu de l'expression enregistrée (lecture seule, tronqué au besoin). */}
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-fg">{t('savedFilters.exprPreview')}</span>
            <code className="block max-h-20 overflow-y-auto break-words rounded-md border border-border bg-surface-2 px-2 py-1 font-mono text-xs text-muted">
              {expr.slice(0, SAVED_FILTER_EXPR_MAX)}
            </code>
          </div>

          {/* Sélecteur d'icône (allow-list) + « aucune ». Coche + aria-checked. */}
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-fg">{t('savedFilters.iconLabel')}</span>
            <div
              role="radiogroup"
              aria-label={t('savedFilters.iconLabel')}
              onKeyDown={onIconKeyDown}
              className="flex flex-wrap gap-1.5"
            >
              <IconChoice
                selected={icon === null}
                label={t('savedFilters.iconNone')}
                onSelect={() => setIcon(null)}
              >
                <NoSymbolIcon className="h-5 w-5" aria-hidden="true" />
              </IconChoice>
              {SAVED_FILTER_ICON_ORDER.map((name_) => (
                <IconChoice
                  key={name_}
                  selected={icon === name_}
                  label={t(SAVED_FILTER_ICON_LABEL_KEY[name_])}
                  onSelect={() => setIcon(name_)}
                >
                  <SavedFilterGlyph icon={name_} className="h-5 w-5" />
                </IconChoice>
              ))}
            </div>
          </div>

          {shownError && (
            <p role="alert" className="flex items-start gap-2 text-danger">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>{shownError}</span>
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            {t('savedFilters.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSave}
            className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            {busy ? t('savedFilters.saving') : t('savedFilters.save')}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

/** Bouton de choix d'icône (radio) : coche + `aria-checked`, bordure `brand` quand actif. */
function IconChoice({
  selected,
  label,
  onSelect,
  children,
}: {
  selected: boolean;
  label: string;
  onSelect: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      tabIndex={rovingTabIndex(selected)}
      onClick={onSelect}
      title={label}
      aria-label={label}
      className={`relative flex h-10 w-10 items-center justify-center rounded-md border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
        selected
          ? 'border-brand bg-brand/10 text-brand'
          : 'border-border text-fg hover:bg-surface-2'
      }`}
    >
      {children}
      {selected && (
        <CheckIcon
          className="absolute -end-1 -top-1 h-4 w-4 rounded-full bg-brand p-0.5 text-brand-fg"
          aria-hidden="true"
        />
      )}
    </button>
  );
}
